import { primaryNav, hotTopics } from "@/fixtures/shared/nav/nav-v2-fixtures";
import { slugForTagTitle } from "@/fixtures/features/topics/tag-fixtures";
import { FILTER_PILLS } from "@/fixtures/live-news/fixtures";
import { DAILYNEWS_ROUTES } from "@/fixtures/dailynews/categories";
import {
  VIDEO_CATEGORIES,
  videoCategoryTabs,
} from "@/fixtures/video/categories";
import { canonicalAuthor } from "@/lib/author-avatars";
/**
 * Canonical internal route paths — QA sweep, 2026-08.
 *
 * WHY THIS EXISTS
 * ---------------
 * Seven call sites hard-coded `"/zh-TW/home"` or `"/zh-TW/onboarding"`. Both
 * were broken:
 *
 *   1. There is no `home` segment. The site index is
 *      `app/[locale]/(public)/page.tsx`, i.e. `/{locale}`, so every one of those
 *      links 404s. QA caught the e-paper logo (ticket: "路由問題，點擊後會跳到
 *      404 頁面，路徑為 .../zh-TW/home"); the other six sit in the auth and
 *      onboarding flows and were never reported.
 *   2. Onboarding lives at `auth/onboarding`, not `/onboarding`.
 *   3. Hard-coding the `zh-TW` prefix defeats next-intl. The app runs
 *      `localePrefix: 'always'` over `['en', 'zh-CN', 'zh-TW']`, so an English
 *      or simplified-Chinese visitor was thrown back into Traditional Chinese.
 *
 * HOW TO USE
 * ----------
 * Pair these with `Link` / `useRouter` from `@/i18n/routing` (NOT `next/link`),
 * which prepend the active locale for you:
 *
 *     import { Link } from "@/i18n/routing";
 *     import { ROUTES } from "@/lib/routes";
 *     <Link href={ROUTES.home}>…</Link>     // -> /zh-TW  (or /en, /zh-CN)
 *
 * Never write a locale segment into a value here.
 */
export const ROUTES = {
  /** Site index — `app/[locale]/(public)/page.tsx`. */
  home: "/",

  // Auth
  login: "/auth/login",
  createAccount: "/auth/create-account",
  enterPassword: "/auth/enter-password",
  forgotPassword: "/auth/forgot-password",
  resetPassword: "/auth/reset-password",
  twoFactor: "/auth/2fa-verification",
  onboarding: "/auth/onboarding",

  // Account
  profile: "/account/profile",
  accountSubscription: "/account/subscription",
  bookmarks: "/account/bookmarks",
  following: "/account/following",
  notifications: "/account/notifications",

  // Checkout
  checkout: "/checkout",
  checkoutSuccess: "/checkout/success",
  /** Failure / cancellation. `?reason=cancelled` switches the copy. */
  checkoutFailed: "/checkout/failed",
  subscription: "/subscription",
  contact: "/contact",

  /** Flip-book e-paper reader — `app/[locale]/(public)/e-paper`. Accepts an
   *  optional `?date=YYYY-MM-DD` naming the issue to open. */
  ePaper: "/e-paper",
} as const;

export type RouteKey = keyof typeof ROUTES;

/**
 * Where a card links when its fixture has no article id of its own.
 *
 * The listing fixtures carry no `href` (this build has no article identifiers),
 * so card mappers default to the article-detail route to make cards clickable
 * at all — QA reported headlines and thumbnails as dead across several pages.
 * Replace the default with the item's real URL once the data provides one.
 *
 * Locale-less: pair with `Link` / `useRouter` from `@/i18n/routing`. Plain <a>
 * consumers (NewsCard) render it as-is, which next-intl's middleware resolves
 * against the active locale.
 */
export const ARTICLE_DETAIL_ROUTE = "/article-detail";

/**
 * EJ Education's OWN article page. `ARTICLE_DETAIL_ROUTE` above is the MAIN
 * site's — 44 files reference it — so a vertical that falls back to it sends
 * the reader out of the brand mid-journey. Brand-scoped constants exist so a
 * new call site inside a vertical cannot reach for the main-site one by
 * reflex. Same locale-less shape and the same caveat as above: it is the
 * "make the card clickable at all" default, to be replaced when the data
 * carries a real URL.
 */
export const EJEDU_ARTICLE_DETAIL_ROUTE = "/ej-education/article-detail";

/** EJ Education's category / author / tag listings. Slugs are
 *  `encodeURIComponent(label)`, matching `authorRoute` above. */
export function ejEduCategoryRoute(label: string) {
  return `/ej-education/category/${encodeURIComponent(label.trim())}`;
}
/**
 * A byline is not always just a name.
 *
 * Fixtures carry `李慧儀 · 中文科主筆` and `黃秀芬 · 校長` — the writer plus their
 * role — and the article page's `hashtag` field does the same. Routing the raw
 * string produced `/ej-education/author/李慧儀%20%C2%B7%20中文科主筆`, which
 * matched no author and 404'd. QA: "Author details page is always 404".
 *
 * The role is display copy, so it is stripped here rather than at each of the
 * dozen call sites: one place to be right, and a new caller cannot forget.
 */
export function ejEduAuthorName(byline: string) {
  return byline.split(/\s*[·・|｜/]\s*/u)[0].trim();
}

export function ejEduAuthorRoute(name: string) {
  return `/ej-education/author/${encodeURIComponent(ejEduAuthorName(name))}`;
}
export function ejEduTagRoute(tag: string) {
  return `/ej-education/tag/${encodeURIComponent(tag.trim())}`;
}

/**
 * EJ Insight's article page. Only ONE article is authored
 * (`articleArticle23`), and `/ejinsight/article/[slug]` `notFound()`s every
 * other slug — 32 fixture hrefs pointed at slugs that do not exist and 404'd.
 * Same shape and same rationale as `ARTICLE_DETAIL_ROUTE` above: point a card
 * at a page that renders, and replace it when the data carries a real one.
 */
/**
 * EJ Tech's own listings.
 *
 * The six section names in the nav all pointed at `/ej-tech/category?type=<x>`
 * — a single page hardcoded to one title and one article list, with two labels
 * sharing a `type` value outright. QA asked for the query param to become a
 * slug segment and for every section to be a real listing.
 */
export function ejTechCategoryRoute(slug: string) {
  return `/ej-tech/category/${slug}`;
}
export function ejTechTagRoute(tag: string) {
  return `/ej-tech/tag/${encodeURIComponent(tag.trim())}`;
}
/*
  HKEJ's advertiser-facing site. The paths mirror the live ones
  (`hkej.com/ratecard/advertising/…`) so inbound links and the company's own
  printed references keep resolving; `next.config.mjs` redirects the legacy
  `.html` filenames onto these.
*/
export const RATECARD_ADVERTISING_ROUTE = "/ratecard/advertising";
export const RATECARD_RATE_ROUTE = "/ratecard/advertising/hkej-rate";
export const RATECARD_TERMS_ROUTE = "/ratecard/advertising/terms";

export const EJTECH_SEARCH_ROUTE = "/ej-tech/search";
/** `HK Startups` is the funding table, not a category listing. */
export const EJTECH_FINANCING_ROUTE = "/ej-tech/financing-record";
/** EJ Tech has one authored article page; cards land there until ids exist. */
export const EJTECH_ARTICLE_ROUTE = "/ej-tech/article";

/**
 * Lifestyle Journal's article page.
 *
 * LJ has one authored article; 31 fixture cards carried `href: "#"`, so most of
 * the magazine's own stories advertised a click and went nowhere — and
 * globals.css gates the hover affordance behind `:not([href="#"])`, so those
 * cards could not respond to the pointer either. Same "land the card somewhere
 * real" rule as `ARTICLE_DETAIL_ROUTE`.
 */
export const LJ_ARTICLE_ROUTE = "/lj/articles/marcus-lau";

/**
 * A VIDEO story. Same page, with `?v=<youtube id>` so the body renders an
 * embed above the text — QA: "click on video item should redirect to article
 * details page (but with a youtube embedded video in the article content)".
 */
export function ljVideoRoute(youtubeId: string) {
  return `${LJ_ARTICLE_ROUTE}?v=${encodeURIComponent(youtubeId)}`;
}

export const LJ_SEARCH_ROUTE = "/lj/search";

/**
 * A blog POST.
 *
 * LJ registers exactly one (`chill-sir-red-wine`), and every blog card in the
 * listing, author and blog-detail fixtures carried `href: "#"` — so the whole
 * 博客 section advertised a click and went nowhere, and globals.css gates the
 * hover affordance behind `:not([href="#"])`, so those cards could not respond
 * to the pointer either. QA: "click on article item to redirect to article
 * details page".
 *
 * It lives under `/lj/articles/` with every other story: a blog post, a video
 * and a news piece are one kind of page to the reader, and were three routes
 * with three copies of the same shell. QA: "no matter it is blog or video or
 * article, all are articles". `/lj/blog/[slug]` redirects here.
 */
export const LJ_BLOG_ROUTE = "/lj/articles/chill-sir-red-wine";

export const EJINSIGHT_ARTICLE_ROUTE = "/ejinsight/article/article-23";
/** The vertical's OWN search. The masthead icon pointed at the main site's
 *  `/search`, which searches the parent paper — the defect QA filed on EJ Tech
 *  and EJ Health as "missing search page". */
export const EJINSIGHT_SEARCH_ROUTE = "/ejinsight/search";

/**
 * An EJ Insight columnist's page.
 *
 * The home rail and grid built this URL by hand as
 * `encodeURIComponent(name)`, which produced
 * `/ejinsight/columnists/Brian%20Y.%20S.%20Wong` — the route keys on a slug,
 * so every one of the six 404'd. Same shape as `ejEduAuthorRoute`: one helper,
 * so a new call site cannot invent a second spelling.
 */
export function ejInsightColumnistRoute(name: string) {
  return `/ejinsight/columnists/${name
    .toLowerCase()
    .replace(/['\u2019.]/g, "")
    .trim()
    .replace(/\s+/g, "-")}`;
}

/** EJ Monthly's own detail page — it exists, and its listing rows were falling
 *  back to the MAIN site's `ARTICLE_DETAIL_ROUTE`. */
export const EJMONTHLY_ARTICLE_ROUTE = "/ej-monthly/article-detail";
/** An issue's contents page. The demo carries one issue, so every archive card
 *  opens it — better than the `"#"` these cards used to carry. */
/* `/ej-monthly/current-issue` was REMOVED — every issue, the latest one
   included, is now `/ej-monthly/monthly/list/[year]/[month]`. The latest
   issue's URL is derived from the fixture as `CURRENT_ISSUE_ROUTE` in
   fixtures/ej-monthly/issue-pages.ts, so it cannot drift from the data. */
export const EJMONTHLY_EPAPER_ROUTE = "/ej-monthly/e-paper";
export const EJMONTHLY_ALL_ISSUES_ROUTE = "/ej-monthly/all-issues";
export const EJMONTHLY_RECOMMENDED_ROUTE = "/ej-monthly/recommended";
/** The magazine's own search results. The masthead panel had suggestions but
 *  no destination — every submit landed on the one article page. */
export const EJMONTHLY_SEARCH_ROUTE = "/ej-monthly/search";
/**
 * Where the magazine's 立即訂閱 goes.
 *
 * `/subscription` is the NEWSPAPER's plan page; a reader subscribing from the
 * magazine picks a magazine first. QA: "when you click on any 立即訂閱 buttons,
 * redirect to /select-magazine" — and the nav's 「預覽 / 訂閱」 with it.
 */
export const EJMONTHLY_SUBSCRIBE_ROUTE = "/select-magazine";

/**
 * One issue's own page. QA: the /all-issues cards must "redirect to the issue
 * detail page with the year and month of the issue selected", and the page
 * takes its cover and contents from the year/month in the URL.
 */
export function ejMonthlyIssueDetailRoute(year: number, month: number): string {
  return `/ej-monthly/monthly/list/${year}/${month}`;
}

/** EJ Health's own detail page. Already hardcoded in several places; named here
 *  so a new call site cannot reach for the main site's by reflex. */
export const EJHEALTH_ARTICLE_ROUTE = "/ej-health/article-detail";

/**
 * The rest of EJ Health's own routes.
 *
 * Five of its card and CTA components defaulted `href` to `"#"` and no call
 * site overrode it, so most of the vertical advertised a click and went
 * nowhere — and globals.css gates the hover affordance behind
 * `:not([href="#"])`, so those cards could not respond to the pointer either.
 * Named here for the same reason as the article route: a new call site should
 * not have to remember the path.
 */
/**
 * A VIDEO article.
 *
 * There is no separate video page any more: `/ej-health/video-detail` was a
 * second article layout that had drifted from the real one and whose player was
 * a poster image with a ▶ drawn on it. A video article is the article page with
 * its cover replaced by the main site's working player.
 */
export const EJHEALTH_VIDEO_ROUTE = "/ej-health/article-detail?video=1";
export const EJHEALTH_DOCTORS_ROUTE = "/ej-health/doctors-lounge";
export const EJHEALTH_NEWS_ROUTE = "/ej-health/medical-news";
/** The vertical's OWN search, not the main site's — QA: "missing search result
 *  page". The masthead used to push to `/search?q=`, which searches HKEJ. */
export const EJHEALTH_SEARCH_ROUTE = "/ej-health/search";

/**
 * A category listing — FLAT, `/ej-health/<slug>`.
 *
 * It used to nest under `/ej-health/topic/<slug>`. 醫健新知 and 醫生會客室 were
 * already flat, and a category is a section of the site rather than a
 * sub-collection of a "topic" page, so all of them sit at the same depth now.
 * Slugs live in `fixtures/ej-health/category/fixtures.ts`; an unknown one 404s
 * there rather than here.
 */
export function ejHealthCategoryRoute(slug: string) {
  return `/ej-health/${encodeURIComponent(slug)}`;
}

/** A tag listing. The slug IS the tag — any tag resolves, so a pill is never a
 *  dead end. See `fixtures/ej-health/tag/fixtures.ts` for why tags and
 *  categories are separate. */
export function ejHealthTagRoute(tag: string) {
  return `/ej-health/tag/${encodeURIComponent(tag)}`;
}

/**
 * Query key that makes an article page mount with its comment drawer open.
 * QA: the comment icon on a card should "redirect to article details page with
 * comment modal opened", so the intent has to survive the navigation.
 */
export const COMMENTS_OPEN_QUERY = "comments";

/**
 * An author's detail page.
 *
 * The detail route treats `[slug]` as a wildcard, so the encoded name keeps the
 * URL readable and stable per author. Never prefix a locale here — `Link` from
 * `@/i18n/routing` adds it (the previous local copy in `authors-grid.tsx`
 * hardcoded `/zh-TW`, which stranded `/en` and `/zh-CN` readers).
 */
/**
 * A topic tag's listing page.
 *
 * NOTE: `app/[locale]/(public)/tags/[slug]` calls `notFound()` for any slug
 * outside `TAG_SLUGS`, which currently contains a single entry (`iran-war`).
 * Every other chip therefore lands on the 404 page until the tag fixtures cover
 * the topics in use — that is a content gap, not a routing bug, and pointing
 * here is the requested behaviour.
 */
/**
 * A topic's tag page.
 *
 * Prefers an authored slug when the label names one (伊朗戰爭 → `iran-war`);
 * otherwise the label itself is the slug and `/tags/[slug]` renders the page
 * under that name — see `getTag` for why it does not 404.
 */
export function tagRoute(tag: string) {
  const label = tag.trim();
  return `/tags/${encodeURIComponent(slugForTagTitle(label) ?? label)}`;
}

/**
 * The feature ("信報專題") detail page a topic card opens.
 *
 * `/features/[slug]` calls `notFound()` for anything outside
 * `TOPIC_DETAIL_SLUGS`, which currently holds a single authored bundle
 * (`budget-2026`). Every topic therefore points at that one page — the same
 * shape as `ARTICLE_DETAIL_ROUTE`, and for the same reason: pointing a card at
 * its "real" slug would 404 until the fixtures cover it.
 */
export const FEATURE_DETAIL_ROUTE = "/features/budget-2026";

/**
 * Where a section-header topic chip ("要聞 - 中東局勢 財政預算案 …") points.
 *
 * Shared by the desktop and mobile section headers so the two never disagree.
 * A caller-supplied `tabHref` wins when it names a real destination; the
 * placeholder `"#"` several callers still pass does not, and falls through to
 * the chip's own listing (or its tag page when it has none).
 */
export function sectionTabRoute(label: string, tabHref?: string): string {
  if (tabHref && tabHref !== "#") return tabHref;
  return categoryRoute(label) ?? tagRoute(label);
}

/** A stock's quote detail page on stock360. */
export function stockQuoteRoute(code: string) {
  return `/stock360/quote/${encodeURIComponent(code.trim())}`;
}

/** A video's detail page. */
export const VIDEO_DETAIL_ROUTE = "/video/detail";

/**
 * A video category's listing.
 *
 * Only `財經` has a dedicated route (`/video/finance`); every other category in
 * the fixtures is a show name with no page of its own, so those fall back to
 * the video index rather than a 404.
 */
const VIDEO_CATEGORY_ROUTES: Record<string, string> = {
  財經: "/video/finance",
  人訪: "/video/people",
  專題: "/video/topics",
};

export function videoCategoryRoute(category: string): string {
  return VIDEO_CATEGORY_ROUTES[category.trim()] ?? "/video";
}

/**
 * A video category listing with one of its tabs preselected.
 *
 * The listing reads `?tab=` (see `app/[locale]/(public)/video/[slug]`), so this
 * is what a breadcrumb like 財經 › 論盡熱話 links its second crumb to. A tab the
 * listing does not offer simply falls back to 全部 there, so this can never
 * 404 — but prefer passing a label the fixtures actually carry.
 */
export function videoTabRoute(category: string, tab: string): string {
  return `${videoCategoryRoute(category)}?tab=${encodeURIComponent(tab.trim())}`;
}

/**
 * A video SHOW name → the listing that offers it, with that tab preselected.
 *
 * Used where only the show is known (a bookmarked video carries its show, not
 * its parent category). Resolved from the fixtures rather than hand-mapped, so
 * a label the listing does not actually offer can never be linked as though it
 * were a tab — those fall back to the video index instead of landing on a page
 * that silently shows 全部.
 */
export function videoShowRoute(show: string): string {
  const label = show.trim();
  for (const [slug, category] of Object.entries(VIDEO_CATEGORIES)) {
    if (videoCategoryTabs(category).includes(label)) {
      return `/video/${slug}?tab=${encodeURIComponent(label)}`;
    }
  }
  return "/video";
}

/**
 * The 即時 feed, filtered to one of its categories.
 *
 * `/live-news` reads `?category=` and its filter row is driven by
 * `FILTER_PILLS`; a label outside that list would render a filter chip that is
 * not in the row and silently show everything, so this returns `undefined` for
 * one and the caller renders a plain label instead.
 */
export function liveNewsCategoryRoute(category: string): string | undefined {
  const label = category.trim();
  if (!label || !LIVE_NEWS_CATEGORIES.has(label)) return undefined;
  return `/live-news?category=${encodeURIComponent(label)}`;
}

/**
 * Where a footer link goes.
 *
 * The footer fixture leaves `href` off every entry and the component fell back
 * to `"#"`, so all ~52 links on every page of the site advertised themselves as
 * clickable and did nothing. Only labels with a page that actually exists are
 * mapped here (each verified to resolve); anything else renders as PLAIN TEXT
 * rather than a dead link — the same rule the card eyebrows already follow.
 */
const FOOTER_ROUTES: Record<string, string> = {
  即時: "/live-news",
  投資: "/wealth-management",
  財經: "/finnews",
  分析: "/columns/news",
  國際: "/finnews",
  港聞: "/finnews/hong-kong",
  地產: "/property",
  科技: "/ej-tech",
  大灣區: "/features",
  商業: "/finnews",
  信報視頻: "/video",
  專欄作者: "/columns/authors",
  最新專欄: "/columns/authors",
  財富管理: "/wealth-management",
  經濟: "/finnews",
  時事: "/columns/news",
  個人理財: "/wealth-management",
  即時報價: "/stock360",
  即市排名: "/stock360/hot-stocks",
  指數: "/stock360/indices",
  股票: "/stock360",
  IPO: "/stock360/ipo",
  外匯: "/stock360/forex",
  我的組合: "/stock360/portfolio",
  選股器: "/stock360/screener",
  信報財經月刊: "/ej-monthly",
  信健康: "/ej-health",
  "EJ TECH": "/ej-tech",
  優雅生活: "/lj",
  Ejinsight: "/ejinsight",
  信報教育: "/ej-education",
  聯絡我們: "/contact",
  訂閱: "/subscription",
  // QA: "add correct navigation link to the footer items". These 11 labels had
  // no entry, so `footerLinkRoute` returned undefined and the footer rendered
  // them as plain text on every page of the site. Each target below is a route
  // that exists — verified by resolving it, not by assuming.
  //
  // EJFQ follows the nav's own brand grid (`brandLogos` in
  // more-menu-fixtures.ts), which already points it at /stock360, so the two
  // cannot drift apart.
  EJFQ: "/stock360",
  輪證: "/stock360/derivatives",
  貴金屬: "/stock360/forex",
  // The policy column. 私隱政策 and 免責聲明 are pages in their own right;
  // 服務協議 is the membership agreement.
  服務協議: "/legal/membership",
  私隱政策: "/legal/privacy",
  免責聲明: "/legal/disclaimer",
  // Was "/contact" — the generic enquiry form — because no advertising page
  // existed. It does now, and it answers this label directly.
  廣告查詢: RATECARD_ADVERTISING_ROUTE,
  關於信報: "/about",
  加入信報: "/careers",
  聯絡信報: "/contact",
};

export function footerLinkRoute(label: string): string | undefined {
  // Zero-width characters survive `trim()` and are invisible in review, so a
  // label carrying one silently renders unlinked. Strip them before lookup.
  const key = label.replace(/[\u200b-\u200d\ufeff]/g, "").trim();
  return FOOTER_ROUTES[key] ?? categoryRoute(key);
}

/** Kept in step with `FILTER_PILLS` in `fixtures/live-news/fixtures.ts`. */
const LIVE_NEWS_CATEGORIES = new Set(
  FILTER_PILLS.filter((p) => p !== "全部"),
);

export function authorRoute(name: string) {
  // Canonicalised, so alias spellings of one writer (杜比 / 杜比Sir) resolve to
  // a single author page rather than two pages for the same person.
  return `/columns/authors/${encodeURIComponent(canonicalAuthor(name))}`;
}

/**
 * Category label → its listing route.
 *
 * QA: "click on category title will redirect to category/subcategory listing
 * page." The mapping is derived from the nav fixtures rather than hand-written,
 * so a category can never point somewhere the nav does not — and labels with no
 * listing (a few fixture-only categories) resolve to `undefined`, which makes
 * the caller render a plain label instead of a dead link.
 */
export function categoryRoute(label: string): string | undefined {
  // Fixtures write eyebrows with trailing separators ("新聞點評 |"), so the raw
  // label is normalised before lookup — otherwise every such card silently
  // renders an unlinked category.
  const key = label.replace(/[|｜/·・\s]+$/u, "").trim();
  if (!key) return undefined;
  // Latin-script eyebrows are written in both cases across the fixtures
  // ("EJ Tech" in one block, "EJ TECH" in the next), so the lookup is folded —
  // otherwise one of the two spellings silently renders as an unlinked label.
  const folded = key.toLowerCase();
  const hit =
    primaryNav.find((n) => n.label.toLowerCase() === folded) ??
    hotTopics.find((n) => n.label.toLowerCase() === folded) ??
    EXTRA_CATEGORY_ROUTES[key] ??
    EXTRA_CATEGORY_ROUTES[folded];
  return typeof hit === "string" ? hit : hit?.href;
}

/**
 * Section labels that appear as card eyebrows but are not nav entries. Kept
 * separate from the nav fixtures so the nav stays the source of truth for
 * anything that IS in the nav.
 */
/** The 日報 categories that have their own listing pages. */
const EXTRA_CATEGORY_ROUTES: Record<string, string> = {
  ...DAILYNEWS_ROUTES,
  // Case-insensitive on the way in (see `categoryRoute`), so one entry covers
  // the home's "EJ Tech" and "EJ TECH" spellings.
  "ej tech": "/ej-tech",
  職場: "/columns/culture",
  香港: "/finnews/hong-kong",
  中國: "/finnews/china",
  美國: "/finnews",
  亞洲: "/finnews",
  歐洲: "/finnews",
  新聞點評: "/columns/news",
  // The article breadcrumb's 專欄 kicker. Without this it fell back to
  // /columns/news, the same page as the 新聞點評 beside it.
  專欄: "/columns/authors",
  // The sponsored-topic showcase's section label. The nav's own 專題 already
  // points at /features; this is the same section spelled out in full.
  信報專題: "/features",
  // finnews block labels — they name sections of that listing, not pages of
  // their own, so they all resolve to it rather than staying unlinked.
  經濟數據: "/finnews",
  機構調查: "/finnews",
  公司動向: "/finnews",
  市場焦點: "/finnews",
  新聞: "/finnews",
  財策: "/finnews",
  專訪: "/columns/news",
  地產即時: "/property/live",
  地產新聞: "/property",
  地產市道: "/property/market",
  專家評論: "/property/experts",
  // The footer's 專欄 column names it 地產評論; the section itself is 專家評論.
  地產評論: "/property/experts",
  睇樓速遞: "/property/tour",
  屋苑樓價: "/property/estate-prices",
  置業工具: "/property/calculator",
  新盤情報: "/property/new-homes",
  二手市場: "/property/secondary",
  工商舖市道: "/property/commercial",
};
